import React from 'react';
import { Badge, Card } from '../../design-system';
import { formatDate } from '../../utils/formatters';
import {
  categoryLabel, displayName, KIND_LABELS, scrubWarnings, testStatusBadge, tierLabel,
} from './lessonReviewLabels';
import styles from './LessonReview.module.css';

/**
 * A list of lesson proposals (the review queue, rejected, or staged). Rows open
 * the proposal drawer. `showReason` adds the reviewer's reason column.
 */
export default function ProposalTable({ proposals, onSelect, emptyText, showReason = false }) {
  if (!proposals || proposals.length === 0) {
    return (
      <Card padding="md">
        <p className={styles.emptyText}>{emptyText}</p>
      </Card>
    );
  }

  return (
    <Card padding="none" className={styles.tableCard}>
      <table className={styles.table}>
        <thead>
          <tr>
            <th className={styles.th}>Lesson</th>
            <th className={styles.th}>Applies to</th>
            <th className={styles.th}>Type</th>
            <th className={styles.th}>Category</th>
            {showReason ? <th className={styles.th}>Reason</th> : <th className={styles.th}>Notes</th>}
            <th className={styles.th}>Suggested</th>
          </tr>
        </thead>
        <tbody>
          {proposals.map((p) => {
            const warnings = scrubWarnings(p.scrub);
            const supporters = Number(p.supporting_sources_count || 1);
            const tests = p.status === 'PENDING' ? testStatusBadge(p) : null;
            return (
              <tr
                key={p.lesson_proposal_id}
                className={styles.row}
                tabIndex={0}
                onClick={() => onSelect(p)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onSelect(p);
                  }
                }}
                aria-label={`Open lesson: ${p.title}`}
              >
                <td className={`${styles.td} ${styles.titleCell}`}>{p.title}</td>
                <td className={styles.td}>
                  <Badge variant={p.target_tier === 'central' ? 'accent' : 'info'} size="sm">
                    {tierLabel(p.target_tier)}
                  </Badge>
                  {p.target_tier === 'customer' && p.customer_id ? (
                    <div className={styles.muted} title={p.customer_id}>{displayName(p.firm_name, 'firm')}</div>
                  ) : null}
                  {p.provenance?.source_case_id ? (
                    <div className={styles.muted} title={p.provenance.source_case_id}>
                      From {displayName(p.provenance.source_case_name, 'case')}
                    </div>
                  ) : null}
                </td>
                <td className={styles.td}>{KIND_LABELS[p.kind] || p.kind}</td>
                <td className={styles.td}>{categoryLabel(p.category)}</td>
                {showReason ? (
                  <td className={styles.td}>{p.status_reason || '—'}</td>
                ) : (
                  <td className={styles.td}>
                    {tests ? (
                      <div><Badge variant={tests.variant} size="sm">{tests.label}</Badge></div>
                    ) : null}
                    {supporters > 1 ? <div>Suggested {supporters} times</div> : null}
                    {warnings.length > 0 ? (
                      <div>
                        {warnings.length} privacy {warnings.length === 1 ? 'note' : 'notes'}
                      </div>
                    ) : null}
                    {p.stale_base ? <div>Based on an older version</div> : null}
                    {!tests && supporters <= 1 && warnings.length === 0 && !p.stale_base ? (
                      <span className={styles.muted}>—</span>
                    ) : null}
                  </td>
                )}
                <td className={`${styles.td} ${styles.nowrap}`}>
                  {p.created_at ? formatDate(p.created_at) : '—'}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </Card>
  );
}
